import { useMemo } from "react";
import { useEvidenceDocuments, useProjectCustomFields, useProjectDocuments } from "@lumanity/ui/evidence";
import { useProject } from "@lumanity/ui/projects";
import { ErrorBanner } from "@lumanity/ui/primitives";
import DocumentUploadPanel from "./DocumentUploadPanel";

interface ProjectUploadProps {
  projectId: string;
  /** Every document saved, queued for indexing and added to the project. */
  onComplete: () => void;
  onCancel?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

/** The Upload step: pick files, review the AI-extracted metadata, save them into the project. */
const ProjectUpload = ({ projectId, onComplete, onCancel, onDirtyChange }: Readonly<ProjectUploadProps>) => {
  const { data: project } = useProject(projectId);
  const { data: projectDocs, error: docsError } = useProjectDocuments(projectId);
  const { data: allDocs = [] } = useEvidenceDocuments();
  const { data: customFields = [], isLoading: fieldsLoading } = useProjectCustomFields(projectId);

  const existingFilenames = useMemo(() => {
    const ids = new Set(projectDocs?.documentIds ?? []);
    return allDocs.filter((d) => ids.has(d.id)).map((d) => d.filename);
  }, [allDocs, projectDocs]);

  if (docsError) return <ErrorBanner message={`Could not load the project's documents: ${docsError.message}`} />;
  if (fieldsLoading) return <div className="text-muted-foreground py-8 text-center">Loading...</div>;

  const projectModel = typeof project?.properties?.model === "string" ? project.properties.model : undefined;

  return (
    <div className="rounded-lg border bg-card p-6">
      <DocumentUploadPanel
        projectId={projectId}
        projectModel={projectModel}
        customFields={customFields}
        existingFilenames={existingFilenames}
        onComplete={onComplete}
        onCancel={onCancel}
        onDirtyChange={onDirtyChange}
      />
    </div>
  );
};

export default ProjectUpload;
